// Authentication routes that should not be rewritten on subdomains
const AUTHENTICATION_ROUTES = [
  '/login',
  '/register',
  '/request-password-reset'
  // NOTE: '/reset-password' is not included here because Supabase logs the user to this route after a password reset request.
];

// Static asset paths that should not be rewritten on subdomains
// Includes Next.js internals, common root files, and project assets
const STATIC_ASSET_PATHS = [
  // Next.js internals
  '/_next', // includes /_next/static and /_next/image
  '/_vercel', // Vercel internals (defensive)
  // API and health
  '/api',
  // Common root files
  '/favicon.ico',
  '/robots.txt',
  '/sitemap.xml',
  '/manifest.json',
  '/manifest.webmanifest',
  '/apple-touch-icon',
  // Project assets
  '/favicon/', // legacy favicon directory
  '/images/',
  '/logos/',
  '/assets/',
  '/fonts/',
  '/static/'
];

// Public paths that are accessible without authentication on restaurant subdomains
// All other paths on subdomains remain protected (e.g., /account-settings, /edit-menu)
const PUBLIC_SUBDOMAIN_PATHS = [
  '/' // Restaurant public page (root only)
];

/**
 * Extracts the root domain from NEXT_PUBLIC_WEBSITE_URL (without port)
 * @returns {string} The root domain (without protocol or port)
 * @throws {Error} If NEXT_PUBLIC_WEBSITE_URL is not set
 * @example
 * // NEXT_PUBLIC_WEBSITE_URL = 'https://resty.digital'
 * getRootDomain() // → 'resty.digital'
 *
 * // NEXT_PUBLIC_WEBSITE_URL = 'http://localhost:3000'
 * getRootDomain() // → 'localhost'
 */
const getRootDomain = () => {
  const baseUrl = process.env.NEXT_PUBLIC_WEBSITE_URL;

  if (!baseUrl) {
    throw new Error('NEXT_PUBLIC_WEBSITE_URL environment variable is required');
  }

  // Remove protocol and port (for subdomain detection/comparison)
  return baseUrl.replace(/^https?:\/\//, '').split(':')[0];
};

/**
 * Extracts the protocol from NEXT_PUBLIC_WEBSITE_URL
 * @returns {string} The protocol ('http' or 'https')
 * @throws {Error} If NEXT_PUBLIC_WEBSITE_URL is not set
 * @example
 * // NEXT_PUBLIC_WEBSITE_URL = 'https://resty.digital'
 * getProtocol() // → 'https'
 *
 * // NEXT_PUBLIC_WEBSITE_URL = 'http://localhost:3000'
 * getProtocol() // → 'http'
 */
const getProtocol = () => {
  const baseUrl = process.env.NEXT_PUBLIC_WEBSITE_URL;

  if (!baseUrl) {
    throw new Error('NEXT_PUBLIC_WEBSITE_URL environment variable is required');
  }

  return baseUrl.startsWith('https') ? 'https' : 'http';
};

/**
 * Extracts the full domain from NEXT_PUBLIC_WEBSITE_URL (with port if present)
 * @returns {string} The full domain (without protocol, but with port if present)
 * @throws {Error} If NEXT_PUBLIC_WEBSITE_URL is not set
 * @example
 * // NEXT_PUBLIC_WEBSITE_URL = 'https://resty.digital'
 * getFullDomain() // → 'resty.digital'
 *
 * // NEXT_PUBLIC_WEBSITE_URL = 'http://localhost:3000'
 * getFullDomain() // → 'localhost:3000'
 */
const getFullDomain = () => {
  const baseUrl = process.env.NEXT_PUBLIC_WEBSITE_URL;

  if (!baseUrl) {
    throw new Error('NEXT_PUBLIC_WEBSITE_URL environment variable is required');
  }

  // Remove protocol only, keep port
  return baseUrl.replace(/^https?:\/\//, '');
};

/**
 * Detects if a hostname has a subdomain (restaurant subdomain)
 * Extracts the first label before the root domain
 * @param {string} hostname - The hostname to check
 * @returns {string|null} The subdomain name or null if on main domain
 */
const detectSubdomain = (hostname) => {
  if (!hostname) {
    return null;
  }

  const rootDomain = getRootDomain();

  // Remove port from hostname if present (e.g., localhost:3000 → localhost)
  const cleanHostname = hostname.split(':')[0];

  // Check if it's exactly the root domain (no subdomain)
  if (cleanHostname === rootDomain) {
    return null;
  }

  // Check if hostname ends with the root domain
  if (cleanHostname.endsWith(`.${rootDomain}`)) {
    // Extract everything before the root domain
    const prefix = cleanHostname.slice(0, -(rootDomain.length + 1));

    // Get the first label (leftmost part)
    // Examples:
    // - 'my-restaurant.resty.digital' → 'my-restaurant'
    // - 'my-restaurant.feature-branch.vercel.app' → 'my-restaurant'
    const firstLabel = prefix.split('.')[0];

    // Return the first label if it exists and is not 'www'
    return (firstLabel && firstLabel !== 'www') ? firstLabel : null;
  }

  return null;
};

/**
 * Checks if a path is an authentication route
 * @param {string} path - The pathname to check
 * @returns {boolean}
 */
const isAuthenticationRoute = path => AUTHENTICATION_ROUTES.includes(path);

/**
 * Checks if a path is a static asset
 * @param {string} path - The pathname to check
 * @returns {boolean}
 */
const isStaticAssetPath = path => STATIC_ASSET_PATHS.some(assetPath => path.startsWith(assetPath));

/**
 * Checks if a path is publicly accessible on restaurant subdomains
 * @param {string} path - The pathname to check
 * @returns {boolean}
 */
const isPublicSubdomainPath = path => PUBLIC_SUBDOMAIN_PATHS.includes(path);

export {
  AUTHENTICATION_ROUTES,
  PUBLIC_SUBDOMAIN_PATHS,
  STATIC_ASSET_PATHS,
  detectSubdomain,
  getFullDomain,
  getProtocol,
  getRootDomain,
  isAuthenticationRoute,
  isPublicSubdomainPath,
  isStaticAssetPath
};
